import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
} from '@mui/material';
import { SkillNode } from '../types/microsoftGraph';

interface SkillsIndexTableProps {
  skillNodes: SkillNode[];
}

export const SkillsIndexTable: React.FC<SkillsIndexTableProps> = ({ 
  skillNodes
}) => {
  const [sortedNodes, setSortedNodes] = useState<SkillNode[]>([]);

  useEffect(() => {
    if (!skillNodes || skillNodes.length === 0) {
      setSortedNodes([]);
      return;
    }

    // ✅ Sort by YPos (row) first, then XPos (column) - matches grid-based numbering
    const sorted = [...skillNodes].sort((a, b) => {
      if (a.YPos !== b.YPos) {
        return a.YPos - b.YPos; // Sort by row (top to bottom)
      }
      return a.XPos - b.XPos; // Then by column (left to right)
    });

    // ✅ Assign sequence numbers (1-based) that match the canvas
    const withSequence = sorted.map((node, index) => ({
      ...node,
      KeyNum: index + 1, // Ensure KeyNum matches the visual order
    }));

    setSortedNodes(withSequence);
  }, [skillNodes]);

  if (sortedNodes.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No skills to display
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Skills Index ({sortedNodes.length} skills)
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Reference guide showing skill numbering and grid positions
        </Typography>
      </Box>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
            <TableCell 
              align="center" 
              sx={{ 
                fontWeight: 'bold',
                minWidth: 50,
                backgroundColor: '#e0e0e0'
              }}
            >
              #
            </TableCell>
            <TableCell 
              sx={{ 
                fontWeight: 'bold',
                minWidth: 250,
                backgroundColor: '#e0e0e0'
              }}
            >
              Skill Name
            </TableCell>
            <TableCell 
              align="center"
              sx={{ 
                fontWeight: 'bold',
                minWidth: 100,
                backgroundColor: '#e0e0e0'
              }}
            >
              Grid Position
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedNodes.map((node) => (
            <TableRow 
              key={node.SkillID} 
              hover
              sx={{
                '&:hover': {
                  backgroundColor: '#f0f7ff',
                  cursor: 'pointer'
                }
              }}
            >
              {/* Key Number - matches canvas */}
              <TableCell 
                align="center" 
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  color: '#1976d2'
                }}
              >
                {node.KeyNum}
              </TableCell>
              
              {/* Skill Name */}
              <TableCell sx={{ fontWeight: 500 }}>
                {node.SkillName}
              </TableCell>
              
              {/* Grid Position (X, Y) */}
              <TableCell align="center">
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontFamily: 'monospace',
                    backgroundColor: '#f5f5f5',
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}
                >
                  ({node.XPos}, {node.YPos})
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SkillsIndexTable;